/**
 * Deciding whether to move the page for someone.
 *
 * Tapping an exercise in the queue selects it, and the panel that lets you enter
 * reps and weight is below the queue — often below the fold on a phone, with the
 * rest timer pinned above it eating more of the screen. So the tap appeared to do
 * nothing: the panel changed somewhere you couldn't see.
 *
 * The rule that stops this becoming its own annoyance is that scrolling is only
 * worth doing when there is something to reveal. Yanking the page when the panel
 * was already sitting in front of you is worse than not scrolling at all — it
 * moves the thing you were about to touch. Hence a measurement rather than an
 * unconditional scroll.
 *
 * DOM-free so the rule is testable; the caller supplies the rectangle.
 */

export function needsReveal(opts: {
  /** The panel's top, in viewport coordinates (getBoundingClientRect().top). */
  top: number
  height: number
  viewportHeight: number
  /**
   * Pixels at the top of the viewport that something is covering — the pinned
   * rest timer. Anything behind it is not visible, however much of it is
   * technically on screen.
   */
  obscuredTop?: number
  /** Below this much visible, it's worth moving the page. */
  minVisible?: number
}): boolean {
  const obscuredTop = opts.obscuredTop ?? 0
  const minVisible = opts.minVisible ?? 120

  const usableTop = Math.max(0, obscuredTop)
  const visible =
    Math.min(opts.top + opts.height, opts.viewportHeight) - Math.max(opts.top, usableTop)

  // Catches both directions with one comparison: a panel below the fold has a
  // tiny or negative overlap, and so does one scrolled off the top. A panel
  // shorter than the threshold can never satisfy it, so cap by its own height.
  return visible < Math.min(minVisible, opts.height)
}
